import { useCallback, useContext, useMemo } from 'react';
import { AuthContext } from '../contexts/AuthContext';
import {
  canAccessUsersRoute,
  canAssignTasks,
  canCreateProjects,
  canCreateTasks,
  canDeleteProjects,
  canDeleteTasks,
  canEditSystemSettings,
  canInviteUsers,
  canManageSecurity,
  canManageSystemRoles,
  canManageUsers,
  canUpdateProjects,
  canUpdateTasks,
  canViewProjects,
  canViewTasks,
  type Capability,
} from '../lib/auth/capabilities';
import { getEffectiveSurfaceCapabilities } from '../lib/auth/effectiveSurfaceCapabilities';
import { useScopedAuthority } from '../lib/auth/useScopedAuthority';
import type { Permission } from './types';

const PERMISSIONS: Permission[] = [
  'tasks.view', 'tasks.create', 'tasks.edit', 'tasks.delete', 'tasks.assign',
  'tasks.manage_all', 'tasks.change_status', 'tasks.bulk_edit', 'tasks.add_comment',
  'projects.view', 'projects.create', 'projects.edit', 'projects.delete', 'projects.manage_members',
  'users.view', 'users.view.page', 'users.create', 'users.edit', 'users.delete',
  'system.manage_settings', 'system.view_audit_logs', 'system.manage_roles',
];

const allows = (permission: Permission, caps: Capability[]): boolean => {
  switch (permission) {
    case 'tasks.view':
    case 'tasks.add_comment': return canViewTasks(caps);
    case 'tasks.create': return canCreateTasks(caps);
    case 'tasks.edit':
    case 'tasks.change_status':
    case 'tasks.bulk_edit': return canUpdateTasks(caps);
    case 'tasks.delete': return canDeleteTasks(caps);
    case 'tasks.assign':
    case 'tasks.manage_all': return canAssignTasks(caps);
    case 'projects.view': return canViewProjects(caps);
    case 'projects.create': return canCreateProjects(caps);
    case 'projects.edit': return canUpdateProjects(caps);
    case 'projects.delete': return canDeleteProjects(caps);
    case 'projects.manage_members': return caps.includes('projects.members.role.change') || caps.includes('projects.members.remove');
    case 'users.view':
    case 'users.view.page': return canAccessUsersRoute(caps);
    case 'users.create': return canInviteUsers(caps);
    case 'users.edit':
    case 'users.delete': return canManageUsers(caps);
    case 'system.manage_settings': return canEditSystemSettings(caps);
    case 'system.view_audit_logs': return canManageSecurity(caps);
    case 'system.manage_roles': return canManageSystemRoles(caps);
    default: return false;
  }
};

export function usePermissions() {
  const auth = useContext(AuthContext);
  const user = auth?.user;
  const { can, authorityProjection } = useScopedAuthority();
  const capabilities = useMemo(
    // Local capability derivation is an explicit demo-only fixture. Connected
    // environments must wait for the server projection and fail closed.
    () => auth?.isDemoMode ? getEffectiveSurfaceCapabilities(user) : [],
    [auth?.isDemoMode, user],
  );
  const userPermissions = useMemo(
    () => user ? PERMISSIONS.filter((permission) => allows(permission, capabilities)) : [],
    [capabilities, user],
  );

  /**
   * CANONICAL METHOD - Scoped capability resolver
   * Use this for all new authorization checks.
   * Returns true only if user can perform capability in the given context.
   * Fails closed (returns false) if context is missing or user lacks access.
   */
  const hasPermission = useCallback(
    (permission: Permission) => userPermissions.includes(permission),
    [userPermissions],
  );
  const hasAllPermissions = useCallback(
    (permissions: Permission[]) => permissions.every((permission) => userPermissions.includes(permission)),
    [userPermissions],
  );
  const hasAnyPermission = useCallback(
    (permissions: Permission[]) => permissions.some((permission) => userPermissions.includes(permission)),
    [userPermissions],
  );
  const isResourceOwner = useCallback(
    (resourceOwnerId: string) => Boolean(user && user.id === resourceOwnerId),
    [user],
  );
  const canAccessResource = useCallback(
    (permission: Permission, resourceOwnerId: string) => userPermissions.includes(permission) || Boolean(user && user.id === resourceOwnerId),
    [user, userPermissions],
  );

  return {
    // CANONICAL - use this for new code
    can,
    
    // Legacy (deprecated) - use only for backward compatibility during migration
    hasPermission,
    hasAllPermissions,
    hasAnyPermission,
    isResourceOwner,
    userPermissions,
    canAccessResource,
    authorityProjection,
  };
}
